import { useEffect, useMemo, useState } from 'react';

export default function InlineEmailOtpVerification({
  email,
  verifiedEmail,
  onVerifiedEmailChange,
  context = 'general',
  className = '',
  mode = 'manual',
}) {
  const normalizedEmail = useMemo(
    () => (typeof email === 'string' ? email.trim().toLowerCase() : ''),
    [email],
  );
  const hasEmail = Boolean(normalizedEmail);
  const isVerified = Boolean(normalizedEmail) && verifiedEmail === normalizedEmail;
  const [otp, setOtp] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [otpRequestedFor, setOtpRequestedFor] = useState('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isOtpFlowVisible, setIsOtpFlowVisible] = useState(mode === 'auto');

  useEffect(() => {
    if (cooldownRemaining <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCooldownRemaining((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [cooldownRemaining]);

  useEffect(() => {
    if (!normalizedEmail) {
      setOtp('');
      setStatus('idle');
      setMessage('');
      setOtpRequestedFor('');
      setCooldownRemaining(0);
      setIsOtpFlowVisible(mode === 'auto');
      if (verifiedEmail) {
        onVerifiedEmailChange('');
      }
      return;
    }

    if (verifiedEmail && verifiedEmail !== normalizedEmail) {
      onVerifiedEmailChange('');
      setOtp('');
      setStatus('idle');
      setMessage('');
      setOtpRequestedFor('');
      setCooldownRemaining(0);
      setIsOtpFlowVisible(mode === 'auto');
    }
  }, [mode, normalizedEmail, onVerifiedEmailChange, verifiedEmail]);

  useEffect(() => {
    if (mode === 'auto') {
      setIsOtpFlowVisible(true);
    }
  }, [mode]);

  const requestOtp = async () => {
    if (!normalizedEmail) {
      setStatus('error');
      setMessage('Enter your email first.');
      setIsOtpFlowVisible(false);
      return;
    }

    setIsOtpFlowVisible(true);
    setStatus('sending');
    setMessage('');

    try {
      const response = await fetch('/api/email-verification/request-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          context,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'Unable to send a verification code right now.');
        if (data.cooldownRemaining) {
          setCooldownRemaining(data.cooldownRemaining);
        }
        return;
      }

      if (data.alreadyVerified) {
        onVerifiedEmailChange(data.email || normalizedEmail);
        setStatus('verified');
        setMessage('Email already verified. You can continue.');
        return;
      }

      setOtpRequestedFor(data.email || normalizedEmail);
      setCooldownRemaining(data.cooldownSeconds || 45);
      setStatus('awaiting_code');
      setMessage(data.message || 'Verification code sent.');
    } catch (error) {
      setStatus('error');
      setMessage('Unable to send the code right now. Please try again.');
    }
  };

  const verifyOtp = async () => {
    if (!otp.trim()) {
      setStatus('error');
      setMessage('Enter the 6-digit code from your email.');
      return;
    }

    setStatus('verifying');
    setMessage('');

    try {
      const response = await fetch('/api/email-verification/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          otp: otp.trim(),
          context,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'Unable to verify the code.');
        return;
      }

      onVerifiedEmailChange(data.email || normalizedEmail);
      setStatus('verified');
      setMessage(data.message || 'Email verified successfully.');
    } catch (error) {
      setStatus('error');
      setMessage('Unable to verify the code. Please try again.');
    }
  };

  const ensureOtpRequested = async () => {
    if (isVerified) {
      return {
        success: true,
        alreadyVerified: true,
      };
    }

    if (!normalizedEmail) {
      setStatus('error');
      setMessage('Enter your email first.');
      setIsOtpFlowVisible(false);
      return {
        success: false,
        message: 'Enter your email first.',
      };
    }

    if (otpRequestedFor === normalizedEmail) {
      return {
        success: true,
        alreadyRequested: true,
      };
    }

    setIsOtpFlowVisible(true);
    setStatus('sending');
    setMessage('');

    try {
      const response = await fetch('/api/email-verification/request-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: normalizedEmail,
          context,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStatus('error');
        setMessage(data.message || 'Unable to send a verification code right now.');
        if (data.cooldownRemaining) {
          setCooldownRemaining(data.cooldownRemaining);
        }
        return {
          success: false,
          message: data.message || 'Unable to send a verification code right now.',
        };
      }

      if (data.alreadyVerified) {
        onVerifiedEmailChange(data.email || normalizedEmail);
        setStatus('verified');
        setMessage('Email already verified. You can continue.');
        return {
          success: true,
          alreadyVerified: true,
        };
      }

      setOtpRequestedFor(data.email || normalizedEmail);
      setCooldownRemaining(data.cooldownSeconds || 45);
      setStatus('awaiting_code');
      setMessage(data.message || 'Verification code sent.');

      return {
        success: true,
        alreadyRequested: false,
      };
    } catch (error) {
      setStatus('error');
      setMessage('Unable to send the code right now. Please try again.');
      return {
        success: false,
        message: 'Unable to send the code right now. Please try again.',
      };
    }
  };

  useEffect(() => {
    if (mode !== 'auto') {
      return;
    }

    if (!normalizedEmail || isVerified || otpRequestedFor === normalizedEmail) {
      return;
    }

    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);
    if (!looksLikeEmail) {
      return;
    }

    const timer = window.setTimeout(() => {
      ensureOtpRequested();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [mode, normalizedEmail, isVerified, otpRequestedFor]);

  return (
    <div className={className}>
      {isVerified ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
          This email is verified and won&apos;t need OTP again.
        </div>
      ) : mode === 'manual' && !isOtpFlowVisible ? (
        hasEmail ? (
        <div className="space-y-2">
          <button
            type="button"
            onClick={requestOtp}
            disabled={status === 'sending' || status === 'verifying' || !normalizedEmail}
            className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-brand-primary/10 px-4 py-2 text-sm font-bold text-brand-primary transition-colors hover:bg-brand-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {status === 'sending' ? 'Sending code...' : 'Verify email with OTP'}
          </button>
          {message && status === 'error' && (
            <p className="text-sm font-medium text-rose-600">{message}</p>
          )}
        </div>
        ) : null
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {mode === 'manual' && (
              <button
                type="button"
                onClick={requestOtp}
                disabled={
                  status === 'sending' ||
                  status === 'verifying' ||
                  !normalizedEmail ||
                  (otpRequestedFor === normalizedEmail && cooldownRemaining > 0)
                }
                className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-brand-primary/10 px-4 py-2 text-sm font-bold text-brand-primary transition-colors hover:bg-brand-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === 'sending'
                  ? 'Sending code...'
                  : otpRequestedFor === normalizedEmail
                    ? cooldownRemaining > 0
                      ? `Resend in ${cooldownRemaining}s`
                    : 'Resend code'
                  : 'Verify email with OTP'}
              </button>
            )}
            {mode === 'auto' && otpRequestedFor === normalizedEmail && (
              <span className="inline-flex items-center rounded-md border border-brand-primary/20 bg-brand-primary/10 px-4 py-2 text-sm font-bold text-brand-primary">
                {status === 'sending'
                  ? 'Sending OTP...'
                  : otpRequestedFor === normalizedEmail
                    ? cooldownRemaining > 0
                      ? `OTP sent. Resend in ${cooldownRemaining}s`
                      : 'OTP sent. You can request a resend.'
                    : 'OTP will be sent automatically'}
              </span>
            )}
            <span className="text-xs text-gray-500">
              We only verify each email once, so future forms won&apos;t ask again.
            </span>
          </div>

          {otpRequestedFor === normalizedEmail && (
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm text-brand-dark focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/10"
              />
              <button
                type="button"
                onClick={verifyOtp}
                disabled={status === 'verifying' || otp.trim().length !== 6}
                className="inline-flex items-center justify-center rounded-md bg-brand-primary px-5 py-3 text-sm font-bold text-white transition-all hover:shadow-lg hover:shadow-brand-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {status === 'verifying' ? 'Verifying...' : 'Confirm OTP'}
              </button>
              <button
                type="button"
                onClick={requestOtp}
                disabled={
                  status === 'sending' ||
                  status === 'verifying' ||
                  (otpRequestedFor === normalizedEmail && cooldownRemaining > 0)
                }
                className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-5 py-3 text-sm font-bold text-brand-primary transition-colors hover:bg-brand-primary/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {cooldownRemaining > 0 ? `Resend in ${cooldownRemaining}s` : 'Resend OTP'}
              </button>
            </div>
          )}

          {message && (
            <p
              className={`text-sm font-medium ${
                status === 'error' ? 'text-rose-600' : 'text-gray-600'
              }`}
            >
              {message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
